import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useSelector } from "react-redux";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Tab,
  Tabs,
  ListGroup,
} from "react-bootstrap";
import useGetAccount from "../../Teams/common/api/useGetAccount";
import FormTextField from "@components/Forms/FormTextField";
import UserDefinedFieldCollection from "@components/UserDefinedFieldCollection/UserDefinedFieldCollection";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { RootState } from "@reducers/rootReducer";
import {
  useCreateProductMutation,
  useGetAccountQuery,
  useUpdateProductMutation,
  useWhoAmIQuery,
  V1EmbeddedProjectVariable,
  V1ProductResource,
} from "@generated";
import { Product, ProductAccountingCode } from "./types";
import "../style.css";
import VersionHistory from "@components/VersionHistory";
import AccountingCodesTable from "./AccountingCodes/accountingCodes";
import VariableRates from "./VariableRates/variableRates";
import { ProjectVariable } from "../../ProfessionalServices/common/types/ProjectVariable";
import ProjectVariablesV2 from "../../../ProjectVariables/ProjectVariablesV2";

interface ProductInfoProps {
  mode: "create" | "edit";
  product: Product;
  setProductAttributes: (product) => void;
  refetch?: () => void;
  allProjectVariables?: ProjectVariable[];
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  mode,
  product,
  setProductAttributes,
  refetch = () => {},
  allProjectVariables,
}) => {
  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;
  const navigate = useNavigate();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { account } = useGetAccount();

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();

  const [activeTab, setActiveTab] = useState<string>("productDetails");

  const formatProductData = (
    data: Product,
    accountId: number
  ): V1ProductResource => {
    let product: V1ProductResource = {
      id: Number(data.id),
      type: "products",
      attributes: {
        name: data.attributes.name,
        description: data.attributes?.description || "",
        sku: data.attributes?.sku || "",
        "product-id": data.attributes?.intProdID || "",
        "manufacturer-part-number": data.attributes?.mfgPartNum || "",
        "unit-of-measure": data.attributes?.units || "",
        category: data.attributes?.category || "",
        subcategory: data.attributes?.subcategory || "",
        "unit-price": data.attributes?.unitPrice || 0,
        "unit-cost": data.attributes?.unitCost || 0,
        "project-variables": (data.attributes?.variables ?? [])
          .map((variable) => {
            if (!variable.name) return undefined;

            if (
              variable.required &&
              (variable["variable-type"] === "date" ||
                variable["variable_type"] === "date") &&
              (!variable.value || variable.value === "")
            ) {
              return {
                ...variable,
                value: new Date().toISOString().split("T")[0],
                name: variable.name,
              };
            }
            return { ...variable, name: variable.name };
          })
          .filter(Boolean) as V1EmbeddedProjectVariable[],
      },
      relationships: {
        account: {
          data: {
            id: Number(accountId),
            type: "accounts",
          },
        },
      },
    };

    return product;
  };

  const handleFailResponse = (msg: string) => {
    setLoading(false);
    setErrorMessage(msg || "Something went wrong.");
    setShowFailAlert(true);
  };

  const handleUpdateProduct = () => {
    setLoading(true);

    if (!account?.id) {
      handleFailResponse("Account must exist to create a product.");
      return;
    }

    // useGetAccount now reads v2, where resource ids are strings; this page
    // still writes through v1, which types them as numbers.
    const data = formatProductData(product, Number(account.id));

    updateProduct({ slug: accountSlug, id: Number(data.id), body: { data } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        const updatedProduct = res?.data;

        if (updatedProduct) {
          setProductAttributes((product) => ({
            ...product,
            attributes: {
              ...product.attributes,
              ...updatedProduct.attributes,
            },
          }));
          setSuccessMessage("Product updated successfully!");
          setShowSuccessAlert(true);
          setTimeout(() => {
            refetch();
          }, 200);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        console.error("Error details:", JSON.stringify(error?.data, null, 2));

        handleFailResponse(
          error?.data?.errors?.[0]?.detail || "An error occured."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveProduct = () => {
    setLoading(true);

    if (!account?.id) {
      handleFailResponse("Account must exist to create a product.");
      return;
    }

    // v1 write path - see the note above on the id type.
    const data = formatProductData(product, Number(account.id));
    delete data.id;

    const newProductData: V1ProductResource = {
      type: "products",
      attributes: {
        name: product.attributes.name,
        description: product.attributes?.description || "",
        sku: product.attributes?.sku || "",
        "product-id": product.attributes?.intProdID || "",
        "manufacturer-part-number": product.attributes?.mfgPartNum || "",
        "unit-of-measure": product.attributes?.units || "",
        category: product.attributes?.category || "",
        subcategory: product.attributes?.subcategory || "",
        "unit-price": product.attributes?.unitPrice || 0,
        "unit-cost": product.attributes?.unitCost || 0,
        "project-variables": product.attributes?.variables ?? [],
      },
      relationships: {
        account: {
          data: {
            id: Number(account.id),
            type: "accounts",
          },
        },
      },
    };
    createProduct({ slug: accountSlug, body: { data: newProductData } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        if (id) {
          navigate(`/admin/materials/${id}/edit`);
        } else {
          handleFailResponse("Failed to create product.");
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        handleFailResponse(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Get Account for version history
  const { data: accountId, isLoading } = useGetAccountQuery({
    slug: accountSlug,
    include: ["resources", "phases"],
  });

  // Get permissions for current user
  const { data: currentUser } = useWhoAmIQuery();
  const permission = currentUser?.data?.attributes?.privileges;
  const auditLogPermission = permission?.find(
    (privilege) => privilege.privilege === "settings.audit_logs"
  )?.["access-level"];

  const renderProductForm = () => (
    <Form>
      <Card.Title
        style={{ color: "#1c2655", padding: "8px 0px", fontWeight: 700 }}
      >
        Identifier
      </Card.Title>
      <Row>
        {Object.entries(product.attributes)
          .filter(([key]) => key === "name" || key === "description")
          .map(([key, value]) => {
            const labelMap: Record<string, string> = {
              name: "*Name",
              description: "Description",
            };

            return (
              <Col key={key}>
                <FormTextField
                  key={key}
                  id={key}
                  label={labelMap[key]}
                  value={value}
                  placeholder={`Enter ${labelMap[key]}`}
                  onChange={(e) => {
                    setProductAttributes({
                      ...product,
                      attributes: {
                        ...product.attributes,
                        [key]: e.target.value,
                      },
                    });
                  }}
                  required={false}
                  readOnly={false}
                />
              </Col>
            );
          })}
      </Row>
      <Row>
        {["mfgPartNum", "intProdID", "sku"].map((key) => (
          <Col key={key}>
            <FormTextField
              id={key}
              label={
                {
                  mfgPartNum: "Manufacturer Part Number",
                  intProdID: "Internal Product ID",
                  sku: "Vendor SKU",
                }[key]
              }
              value={
                product.attributes[key as keyof typeof product.attributes] || ""
              }
              placeholder={`Enter ${
                {
                  mfgPartNum: "Manufacturer Part Number",
                  intProdID: "Internal Product ID",
                  sku: "Vendor SKU",
                }[key]
              }`}
              onChange={(e) => {
                setProductAttributes({
                  ...product,
                  attributes: { ...product.attributes, [key]: e.target.value },
                });
              }}
              required={false}
              readOnly={false}
            />
          </Col>
        ))}
      </Row>
      {/* @ts-ignore */}
      {allProjectVariables?.length > 0 && <hr />}
      <UserDefinedFieldCollection
        userDefinedFields={
          product?.attributes?.variables || allProjectVariables
        }
        disabled={false}
        sectionTitle={"User Defined Fields"}
        onChange={(userDefinedField) => {
          setProductAttributes({
            ...product,
            attributes: {
              ...product.attributes,
              variables: product?.attributes?.variables?.map((originalPv) => {
                return originalPv.name == userDefinedField.name
                  ? userDefinedField
                  : originalPv;
              }),
            },
          });
        }}
      />
      <hr />
      <Card.Title
        style={{ color: "#1c2655", padding: "8px 0px", fontWeight: 700 }}
      >
        Classification
      </Card.Title>
      <Row>
        {Object.entries(product.attributes)
          .filter(([key]) => key === "category" || key === "subcategory")
          .map(([key, value]) => {
            const labelMap: Record<string, string> = {
              category: "Category",
              subcategory: "Subcategory",
            };

            return (
              <Col key={key}>
                <FormTextField
                  key={key}
                  id={key}
                  label={labelMap[key]}
                  value={value}
                  placeholder={`Enter ${labelMap[key]}`}
                  onChange={(e) => {
                    setProductAttributes({
                      ...product,
                      attributes: {
                        ...product.attributes,
                        [key]: e.target.value,
                      },
                    });
                  }}
                  required={false}
                  readOnly={false}
                />
              </Col>
            );
          })}
      </Row>
      <hr />
      <Card.Title
        style={{
          color: "#1c2655",
          padding: "8px 0px",
          fontWeight: 700,
          marginBottom: "16px",
        }}
      >
        Price
      </Card.Title>
      <Row className="priceTabs">
        <Tab.Container id="list-group-tabs-example" defaultActiveKey="#link1">
          <Tab.Content>
            <Tab.Pane className="priceTabBackground" eventKey="#link1">
              <div className="priceTabContent">
                {Object.entries(product.attributes)
                  .filter(([key]) => key === "unitPrice" || key === "unitCost")
                  .map(([key, value]) => {
                    const labelMap: Record<string, string> = {
                      unitPrice: "Unit Price",
                      unitCost: "Unit Cost",
                    };

                    return (
                      <Col key={key}>
                        <FormTextField
                          key={key}
                          id={key}
                          label={labelMap[key]}
                          value={value}
                          placeholder={`Enter ${labelMap[key]}`}
                          onChange={(e) => {
                            setProductAttributes({
                              ...product,
                              attributes: {
                                ...product.attributes,
                                [key]: e.target.value,
                              },
                            });
                          }}
                          required={false}
                          readOnly={false}
                        />
                      </Col>
                    );
                  })}
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Row>
    </Form>
  );

  return (
    <div className="legacyPageBleed">
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Card
        style={{
          padding: "16px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: "var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "40px",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              className="accordianTitle"
              style={{ height: "40px", margin: "0", alignContent: "center" }}
            >
              <Button
                className="cancelButton"
                variant="secondary"
                onClick={() =>
                  window.location.replace(`${appHost}/admin/materials`)
                }
              >
                Cancel
              </Button>
              {mode === "edit" ? `Edit Product` : "Add Product"}
            </div>

            {activeTab === "productDetails" && (
              <div className="actionBtns">
                <Button
                  className="regButton"
                  onClick={() =>
                    mode === "edit"
                      ? handleUpdateProduct()
                      : handleSaveProduct()
                  }
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? <SmallSpinner /> : "Save"}
                </Button>
              </div>
            )}
          </div>
          <hr />
        </Card.Header>
        <div>
          {mode === "edit" ? (
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k ?? "productDetails")}
              id="product-tabs"
              className="mb-3"
            >
              <Tab
                className="prodDetailTab"
                eventKey="productDetails"
                title="Product Details"
              >
                <hr style={{ marginTop: "0.5rem" }} />
                <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
                  {renderProductForm()}
                </Card.Body>
              </Tab>

              <Tab
                className="varRateTab"
                eventKey="variableRates"
                title="Variable Rates"
              >
                <hr style={{ marginTop: "0.5rem" }} />
                <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
                  {activeTab === "variableRates" ? (
                    isLoading ? (
                      <SmallSpinner />
                    ) : (
                      <VariableRates
                        productId={product.id}
                        refetchProduct={refetch}
                      />
                    )
                  ) : (
                    <p className="text-muted">
                      Switch to this tab to view Variable Rates.
                    </p>
                  )}
                </Card.Body>
              </Tab>

              <Tab
                className="acctCodesTab"
                eventKey="accountingCodes"
                title="Accounting Codes"
              >
                <hr style={{ marginTop: "0.5rem" }} />
                <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
                  {activeTab === "accountingCodes" ? (
                    isLoading ? (
                      <SmallSpinner />
                    ) : (
                      <AccountingCodesTable
                        productId={product.id}
                        onSave={() => refetch()}
                      />
                    )
                  ) : (
                    <p className="text-muted">
                      Switch to this tab to view Accounting Codes.
                    </p>
                  )}
                </Card.Body>
              </Tab>

              {auditLogPermission === "none" ? null : (
                <Tab
                  className="prodVersionsTab"
                  eventKey="productVersions"
                  title="Product Versions"
                >
                  <hr style={{ marginTop: "0.5rem" }} />
                  <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
                    <div style={{ marginTop: "20px" }}>
                      {isLoading ? (
                        <SmallSpinner />
                      ) : (
                        <VersionHistory
                          filter={{
                            "context-type": "Material",
                            "context-id": product?.id,
                          }}
                          permission={auditLogPermission === "manage"}
                          account={accountId?.data}
                        />
                      )}
                    </div>
                  </Card.Body>
                </Tab>
              )}
            </Tabs>
          ) : (
            <>{renderProductForm()}</>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProductInfo;
